// Normal imports
import {
  getCustomizeControlsActions,
  getCustomizeControlsAiOpenStates,
  useCustomizeControlsStore,
  TSCENUM,
  TSCABBRIVATIONS,
} from "@/modules/agent/states/cusomize-controls";
import type {
  SecurityControlCategory,
  Soc2Control,
  VendorAssessmentControl,
  NistCsfControl
} from "@/modules/agent/states/cusomize-controls";
import { AgentSessionStepType, AgentSessionType, type AgentSessionStep } from "@/modules/sessions/types";
import { cn } from "@/shared/utils/classname-merger";
import { Checkbox } from "@nextui-org/react";
import {
  CustomizeControlCategoryRenderer,
  CustomizeControlDescriptionRenderer,
  CustomizeControlIdRenderer,
  CustomizeControlSoc2DocumentUploadRenderer,
  CustomizeControlTestingProcedureRenderer,
  CustomizeCustomAssessmentControlIdRenderer,
  EditableColumnRenderer,
  CustomizeControlCheckboxRenderer,
} from "../column-cell-renderers/soc2-renderers";
import {
  CustomizeControlVendorCategoryRenderer,
  CustomizeControlVendorControlDescriptionRenderer,
} from "../column-cell-renderers/vendor-assessment-renderers";
import {
  CustomizeControlNistCsfIdRenderer,
  CustomizeControlNistCsfCategoryRenderer,
  CustomizeControlNistCsfFunctionRenderer,
  CustomizeControlNistCsfDocumentUploadRenderer
} from "../column-cell-renderers/nist-csf-renderers";
import { ZButton } from "@/shared/components/button";
import { useState } from "react";
import UploadDocumentsModal from "../../customize-controls/UploadDocumentsModal";
import { getAgentStepData, getAgentData } from "@/modules/agent/states";
import { extractAgentId } from "@/modules/agent/utils";

// Type guard to check if a step is a customize controls step
function isCustomizeControlsStep(step: AgentSessionStep): step is AgentSessionStep & { type: AgentSessionStepType.CONTROL_SELECTION | AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING; data: { url: string | null; metadata?: any } } {
  return (step.type === AgentSessionStepType.CONTROL_SELECTION || step.type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING) && 
         step.data && 
         typeof step.data === 'object' && 
         'url' in step.data;
}

export const customizeControlsColumns = ({
  accordianOpenState,
  handleChangeDataState,
  searchQuery,
  selectedControls,
  setAccordianOpenState,
  setSelectedControls,
  filteredRows,
  showCheckboxes = false, // Keep default as false
  isDisabled = false,
  activeBulkAction = "",
  agentId,
  isReassessmentMode = false,
}: {
  accordianOpenState: {
    security: boolean;
    availability: boolean;
    confidentiality: boolean;
    "processing integrity": boolean;
    privacy: boolean;
  };
  handleChangeDataState: (
    key: keyof Soc2Control,
    uId: string,
    newValue: string,
    changesInTestingProcedure?: number
  ) => void;
  searchQuery: string;
  selectedControls: Set<string>;
  setAccordianOpenState: (key: TSCENUM, newValue: boolean) => void;
  setSelectedControls: (
    newVal: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
  filteredRows: Soc2Control[];
  showCheckboxes?: boolean; // Keep default as false
  isDisabled?: boolean;
  activeBulkAction: string;
  agentId?: string;
  isReassessmentMode?: boolean;
}) => {
  return [
    {
      fieldName: "checkbox",
      headerName: "",
      headerClassName: "w-[40px]",
      renderCell: (control: Soc2Control) => (
        showCheckboxes && (activeBulkAction !== "upload" || !!(control.tsc_id && (control.tsc !== TSCENUM.SECURITY || control.category) && control.control)) ? (
          <CustomizeControlCheckboxRenderer
            control={control}
            selectedControls={selectedControls}
            setSelectedControls={setSelectedControls}
            handleChangeDataState={handleChangeDataState}
            searchQuery={searchQuery}
          />
        ) : (
          <div className="min-h-[40px]"></div>
        )
      ),
      renderHeader: () => {
        const isCheckboxDisabled = getCustomizeControlsAiOpenStates() > 0;
        
        // Check if we're in reassessment mode for SOC1/SOC2 agent types
        const agentData = getAgentData(agentId || "");
        const isSOC1OrSOC2Agent = agentData?.sessionData.type === AgentSessionType.SOC2_GAP ||
                                  agentData?.sessionData.type === AgentSessionType.SOC2_GAP_TYPE1 ||
                                  agentData?.sessionData.type === AgentSessionType.SOC2_GAP_TYPE2;
        
        // Enable checkboxes in reassessment mode for SOC1/SOC2 agents
        const shouldEnableCheckboxInReassessment = isReassessmentMode && isSOC1OrSOC2Agent;
        const isHeaderCheckboxDisabled = isCheckboxDisabled || (isReassessmentMode && !shouldEnableCheckboxInReassessment);
        
        return (
          <div className="flex justify-center">
            {showCheckboxes && (
              <Checkbox
                isSelected={
                  filteredRows.length === selectedControls.size &&
                  selectedControls.size > 0
                }
                isIndeterminate={
                  selectedControls.size > 0 &&
                  filteredRows.length !== selectedControls.size
                }
                classNames={{
                  base: cn("group p-0"),
                  label: "p-0",
                  wrapper: cn(
                    "group-data-[disabled=true]:border-default",
                    "after:group-data-[disabled=true]:bg-default-100"
                  ),
                  icon: "group-data-[disabled=true]:text-default-300",
                }}
                onChange={() => {
                  if (filteredRows.length === selectedControls.size) {
                    setSelectedControls(new Set([]));
                  } else {
                    const filteredSet = new Set<string>();
                    filteredRows.forEach(d => {
                      if(d.uid && ((d.tsc_id && (d.tsc !== TSCENUM.SECURITY || d.category) && d.control))){
                        filteredSet.add(d.uid)
                      }
                    })
                    setSelectedControls(filteredSet);
                  }
                }}
                data-component="z-table-row-select"
                color={"primary"}
                isDisabled={searchQuery.length > 0 || isHeaderCheckboxDisabled}
                disabled={searchQuery.length > 0 || isHeaderCheckboxDisabled}
              />
            )}
          </div>
        );
      },
    },
    {
      fieldName: "id",
      headerName: "id",
      headerClassName: "w-[40px] pr-0", // Consistent ID column width
      renderCell: (control: Soc2Control) => (
        <CustomizeControlIdRenderer
          accordianOpenState={accordianOpenState}
          control={control}
          handleChangeDataState={handleChangeDataState}
          searchQuery={searchQuery}
          setAccordianOpenState={setAccordianOpenState}
          isDisabled={isDisabled}
        />
      ),
      renderHeader: () => (
        <div className="text-[12px] leading-5 font-semibold">ID</div>
      ),
    },
    {
      fieldName: "category",
      headerName: "Category",
      headerClassName: "w-[240px]", // Ensure 240px width for SOC2
      renderCell: (control: Soc2Control) => (
        <CustomizeControlCategoryRenderer
          control={control}
          handleChangeDataState={handleChangeDataState}
          searchQuery={searchQuery}
          isDisabled={isDisabled}
        />
      ),
    },
    {
      fieldName: "control",
      headerName: "Control Description",
      // headerClassName: "p-1",
      minWidth: 280, // Reduced minimum width to maximize available space
      renderCell: (control: Soc2Control) => (
        <CustomizeControlDescriptionRenderer
          control={control}
          handleChangeDataState={handleChangeDataState}
          searchQuery={searchQuery}
          isDisabled={isDisabled}
        />
      ),
    },
    {
      fieldName: "testing_procedure",
      headerName: "Testing Procedure",
      headerClassName: "p-1",
      minWidth: 280,
      renderCell: (control: Soc2Control) => (
        <CustomizeControlTestingProcedureRenderer
          control={control}
          handleChangeDataState={handleChangeDataState}
          searchQuery={searchQuery}
          isDisabled={isDisabled}
        />
      ),
    },
    {
      fieldName: "documents",
      headerName: "Documents",
      headerClassName: "w-[100px]",
      renderCell: (control: Soc2Control) => {
        const currentIndex = filteredRows.findIndex(c => c.uid === control.uid);
        return !!(control.tsc_id && (control.tsc !== TSCENUM.SECURITY || control.category) && control.control && control.toe) && (
          <CustomizeControlSoc2DocumentUploadRenderer 
            control={control} 
            allControls={filteredRows}
            currentIndex={currentIndex}
          />
        );
      },
      renderHeader: () => (
        <div className="text-[12px] leading-5 font-semibold">Documents</div>
      ),
    }
  ];
};

export const vendorAssessmentControlsColumns = ({
  handleChangeDataState,
  searchQuery,
  selectedControls,
  setSelectedControls,
  filteredRows,
  showCheckboxes = false, // Change default to false for consistency
  isDisabled = false,
}: {
  handleChangeDataState: (
    key: keyof VendorAssessmentControl,
    uId: string,
    newValue: string,
    changesInTestingProcedure?: number
  ) => void;
  searchQuery: string;
  selectedControls: Set<string>;
  setSelectedControls: (
    newVal: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
  filteredRows: VendorAssessmentControl[];
  showCheckboxes?: boolean;
  isDisabled?: boolean;
}) => {
  return [
    {
      fieldName: "category",
      headerName: "category",
      headerClassName: "w-[240px]", // Ensure header width matches
      renderCell: (control: VendorAssessmentControl) => (
        <CustomizeControlVendorCategoryRenderer
          control={control}
          handleChangeDataState={handleChangeDataState}
          searchQuery={searchQuery}
          selectedControls={selectedControls}
          setSelectedControls={setSelectedControls}
          isDisabled={isDisabled}
          // Remove selectedTab
        />
      ),
      renderHeader: () => {
        const isCheckboxDisabled = getCustomizeControlsAiOpenStates() > 0;
        return (
          <div className="flex w-[240px]"> {/* Updated width to match */}
            {showCheckboxes && (
              <Checkbox
                isSelected={
                  filteredRows.length === selectedControls.size &&
                  selectedControls.size > 0
                }
                isIndeterminate={
                  selectedControls.size > 0 &&
                  filteredRows.length !== selectedControls.size
                }
                classNames={{
                  base: cn("group"),
                  wrapper: cn(
                    "group-data-[disabled=true]:border-default",
                    "after:group-data-[disabled=true]:bg-default-100"
                  ),
                  icon: "group-data-[disabled=true]:text-default-300",
                }}
                onChange={() => {
                  if (filteredRows.length === selectedControls.size) {
                    setSelectedControls(new Set([]));
                  } else {
                    setSelectedControls(new Set(filteredRows.map((d) => d.uid)));
                  }
                }}
                data-component="z-table-row-select"
                color={"primary"}
                disabled={searchQuery.length > 0 || isCheckboxDisabled}
                isDisabled={searchQuery.length > 0 || isCheckboxDisabled}
              />
            )}
            <div className="text-[12px] leading-5 font-semibold">Category</div>
          </div>
        );
      },
    },
    {
      fieldName: "control",
      headerName: "Control Description",
      headerClassName: "pl-1",
      minWidth: 420, // Reduced to maximize space
      renderCell: (control: VendorAssessmentControl) => (
        <CustomizeControlVendorControlDescriptionRenderer
          control={control}
          handleChangeDataState={handleChangeDataState}
          searchQuery={searchQuery}
          isDisabled={isDisabled}
        />
      ),
    },
  ];
};

export const nistCsfControlsColumns = ({
  handleChangeDataState,
  searchQuery,
  selectedControls,
  setSelectedControls,
  filteredRows,
  selectedTab,
  showCheckboxes = false,
}: {
  handleChangeDataState: (
    key: keyof NistCsfControl,
    uId: string,
    newValue: string,
    changesInControl?: number
  ) => void;
  searchQuery: string;
  selectedControls: Set<string>;
  setSelectedControls: (
    newVal: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
  filteredRows: NistCsfControl[];
  selectedTab: string | null;
  showCheckboxes?: boolean;
}) => {
  return [
    {
      fieldName: "control_id",
      headerName: "ID",
      headerClassName: "w-[100px] pr-0", // Adjusted for better spacing
      renderCell: (control: NistCsfControl) => (
        <CustomizeControlNistCsfIdRenderer
          control={control}
          handleChangeDataState={handleChangeDataState}
          searchQuery={searchQuery}
          selectedControls={selectedControls}
          setSelectedControls={setSelectedControls}
          showCheckboxes={showCheckboxes}
        />
      ),
      renderHeader: () => {
        const isCheckboxDisabled = getCustomizeControlsAiOpenStates() > 0;
        return (
          <div className="flex pl-1 items-center"> {/* Reduced padding from pl-4 to pl-1 */}
            {showCheckboxes && (
              <Checkbox
                isSelected={
                  filteredRows.length === selectedControls.size &&
                  selectedControls.size > 0
                }
                isIndeterminate={
                  selectedControls.size > 0 &&
                  filteredRows.length !== selectedControls.size
                }
                classNames={{
                  base: cn("group pl-1"),
                  wrapper: cn(
                    "group-data-[disabled=true]:border-default",
                    "after:group-data-[disabled=true]:bg-default-100"
                  ),
                  icon: "group-data-[disabled=true]:text-default-300",
                }}
                onChange={() => {
                  if (filteredRows.length === selectedControls.size) {
                    setSelectedControls(new Set([]));
                  } else {
                    setSelectedControls(new Set(filteredRows.map((d) => d.uid)));
                  }
                }}
                data-component="z-table-row-select"
                color={"primary"}
                disabled={ isCheckboxDisabled}
                isDisabled={ isCheckboxDisabled}
              />
            )}
            <div className="text-[12px] leading-5 font-semibold pl-1"> {/* Reduced padding */}
              ID
            </div>
          </div>
        );
      },
    },
    {
      fieldName: "category",
      headerName: "Category",
      headerClassName: "w-[40%] p-1", // Consistent 240px width with SOC2
      renderCell: (control: NistCsfControl) => (
        <CustomizeControlNistCsfCategoryRenderer
          control={control}
          handleChangeDataState={handleChangeDataState}
          searchQuery={searchQuery}
        />
      ),
    },
    {
      fieldName: "function",
      headerName: "Description",
      headerClassName: "flex-1 p-1", // Use remaining space
      renderCell: (control: NistCsfControl) => (
        <CustomizeControlNistCsfFunctionRenderer
          control={control}
          handleChangeDataState={handleChangeDataState}
          searchQuery={searchQuery}
        />
      ),
    },
    {
      fieldName: "documents",
      headerName: "Documents",
      headerClassName: "w-[80px]", // Slightly increased for better spacing
      renderCell: (control: NistCsfControl) => {
        const currentIndex = filteredRows.findIndex(c => c.uid === control.uid);
        return (
          <CustomizeControlNistCsfDocumentUploadRenderer 
            control={control} 
            allControls={filteredRows}
            currentIndex={currentIndex}
          />
        );
      },
      renderHeader: () => (
        <div className="text-[12px] leading-5 font-semibold">Documents</div>
      ),
    }
  ];
};

export const gapCustomControlsColumns = ({
  accordianOpenState,
  filteredRows,
  handleChangeDataState,
  searchQuery,
  selectedControls,
  setAccordianOpenState,
  setSelectedControls,
  showCheckboxes,
  isDisabled,
  activeBulkAction,
  agentId,
  isReassessmentMode
}: {
  accordianOpenState: {
    security: boolean;
    availability: boolean;
    confidentiality: boolean;
    "processing integrity": boolean;
    privacy: boolean;
  };
  filteredRows: Soc2Control[];
  handleChangeDataState: (
    key: keyof Soc2Control,
    uId: string,
    newValue: string,
    changesInTestingProcedure?: number
  ) => void;
  searchQuery: string;
  selectedControls: Set<string>;
  setAccordianOpenState: (key: TSCENUM, newValue: boolean) => void;
  setSelectedControls: (
    newVal: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
  showCheckboxes: boolean;
  isDisabled: boolean;
  activeBulkAction: string;
  agentId?: string;
  isReassessmentMode?: boolean;
}) => {
  
  const stepData = getAgentStepData(extractAgentId(location.pathname) as string);
  const customizeControlStep = stepData?.find(x => x.type === AgentSessionStepType.CONTROL_SELECTION);
  const customColumnsMetaData = (customizeControlStep && isCustomizeControlsStep(customizeControlStep)) ? customizeControlStep.data.metadata : undefined;

    const defaultKeys = new Set(["control_id", "control_description"])

    const customColumns: any[] = [];
  if (Array.isArray(customColumnsMetaData)) {
    customColumnsMetaData
      .filter(col => !defaultKeys.has(col.key))
      .forEach(col => {
        customColumns.push({
          fieldName: col.key,
          headerName: col.ui_key || col.key,
          minWidth: 450,
          headerClassName: "min-w-[250px]",
          renderCell: (control: Soc2Control) => (
            <EditableColumnRenderer
              columnKey={col.ui_key}
              handleChangeDataState={handleChangeDataState}
              isDisabled={isDisabled}
              label={col.ui_key || col.key}
              placeholder={col.ui_key || col.key}
              control={control}
              searchQuery={searchQuery}
            />
          ),
        });
      });
  }


  return [
    {
      fieldName: "checkbox",
      headerName: "",
      headerClassName: "w-[40px]",
      renderCell: (control: Soc2Control) => (
        showCheckboxes && (activeBulkAction !== "upload" || !!(control.control_id && control.control)) ? (
          <CustomizeControlCheckboxRenderer
            control={control}
            selectedControls={selectedControls}
            setSelectedControls={setSelectedControls}
            handleChangeDataState={handleChangeDataState}
            searchQuery={searchQuery}
          />
        ) : (
          <div className="w-[40px] min-h-[40px]"></div>
        )
      ),
      renderHeader: () => {
        // Check if we're in reassessment mode for SOC1/SOC2 agent types
        const agentData = getAgentData(agentId || "");
        const isSOC1OrSOC2Agent = agentData?.sessionData.type === AgentSessionType.SOC2_GAP ||
                                  agentData?.sessionData.type === AgentSessionType.SOC2_GAP_TYPE1 ||
                                  agentData?.sessionData.type === AgentSessionType.SOC2_GAP_TYPE2;
        
        // Enable checkboxes in reassessment mode for SOC1/SOC2 agents
        const shouldEnableCheckboxInReassessment = isReassessmentMode && isSOC1OrSOC2Agent;
        const isHeaderCheckboxDisabled = isReassessmentMode && !shouldEnableCheckboxInReassessment;
        
        return (
          <div className="flex w-[40px] justify-center">
            {showCheckboxes && (
              <Checkbox
                isSelected={
                  filteredRows.length === selectedControls.size &&
                  selectedControls.size > 0
                }
                isIndeterminate={
                  selectedControls.size > 0 &&
                  filteredRows.length !== selectedControls.size
                }
                classNames={{
                  base: cn("group"),
                  wrapper: cn(
                    "group-data-[disabled=true]:border-default",
                    "after:group-data-[disabled=true]:bg-default-100"
                  ),
                  icon: "group-data-[disabled=true]:text-default-300",
                }}
                onChange={() => {
                  if (filteredRows.length === selectedControls.size) {
                    setSelectedControls(new Set([]));
                  } else {
                    const filteredSet = new Set<string>();
                    filteredRows.forEach(d => {
                      if(d.uid && ((d.control_id && d.control))){
                        filteredSet.add(d.uid)
                      }
                    })
                    setSelectedControls(filteredSet);
                  }
                }}
                data-component="z-table-row-select"
                color={"primary"}
                isDisabled={searchQuery.length > 0 || isHeaderCheckboxDisabled}
                disabled={searchQuery.length > 0 || isHeaderCheckboxDisabled}
              />
            )}
          </div>
        );
      },
    },
    {
      fieldName: "control_id",
      headerName: "id",
      headerClassName: "w-[120px] pr-0", // Consistent ID column width
      renderCell: (control: Soc2Control) => (
        <CustomizeCustomAssessmentControlIdRenderer
          control={control}
          handleChangeDataState={handleChangeDataState}
          searchQuery={searchQuery}
          isDisabled={isDisabled}
        />
      ),
      renderHeader: () => (
        <div className="text-[12px] leading-5 font-semibold">ID</div>
      ),
    },
    {
      fieldName: "control",
      headerName: "Control Description",
      // headerClassName: "p-1",
      minWidth: 280, // Reduced minimum width to maximize available space
      renderCell: (control: Soc2Control) => (
        <CustomizeControlDescriptionRenderer
          control={control}
          handleChangeDataState={handleChangeDataState}
          searchQuery={searchQuery}
          isDisabled={isDisabled}
        />
      ),
    },
    ...customColumns,
    {
      fieldName: "documents",
      headerName: "Documents",
      headerClassName: "w-[90px] sticky-right",
      maxWidth: "90",
      renderCell: (control: Soc2Control) => {
        const currentIndex = filteredRows.findIndex(c => c.uid === control.uid);
        return control.control_id && control.control && (
          <div className="bg-inherit max-w-[100px]">
            <CustomizeControlSoc2DocumentUploadRenderer 
            control={control} 
            allControls={filteredRows}
              currentIndex={currentIndex}
          />
          </div>
        );
      },
      renderHeader: () => (
        <div className="text-[12px] leading-5 font-semibold sticky-right">Documents</div>
      ),
    }
  ];
};

export const searchAndFilteringList = (agentType: AgentSessionType | null) => {
  switch (agentType) {
    case AgentSessionType.SOC2_GAP_TYPE1:
    case AgentSessionType.SOC2_GAP_TYPE2:
    case AgentSessionType.SOC2_GAP:
      return [
        "category",
        "toe",
        "control",
        "control_id",
        "tsc",
        "tsc_id",
        "zania_control_id",
      ];
    case AgentSessionType.GAP_CUSTOM_FRAMEWORK:
      const stepData = getAgentStepData(extractAgentId(location.pathname) as string);
      const customizeControlStep = stepData?.find(x => x.type === AgentSessionStepType.CONTROL_SELECTION);
      const customColumnsMetaData = (customizeControlStep && isCustomizeControlsStep(customizeControlStep)) ? customizeControlStep.data.metadata : undefined;
    
      return customColumnsMetaData?.map((x: any) => x.key) || []
    case AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT:
      return ["category", "question"];
    case AgentSessionType.RISK_ASSESSMENT:
      return ["control_id", "category", "function"];
    default:
      return [];
  }
};

type updateHandlerForSoc2 = {
  agentType:
    | AgentSessionType.SOC2_GAP_TYPE1
    | AgentSessionType.SOC2_GAP_TYPE2
    | AgentSessionType.SOC2_GAP;
  key: keyof Soc2Control;
  // sectionWiseData: Soc2Control[];
  uId: string;
  newValue: string;
  changesInTestingProcedure?: number;
};

type updateHandlerForCustomGapAssessment = {
  agentType: AgentSessionType.GAP_CUSTOM_FRAMEWORK;
  key: keyof Soc2Control;
  // sectionWiseData: Soc2Control[];
  uId: string;
  newValue: string;
  changesInTestingProcedure?: number;
};

type updateHandleForVendorAssesment = {
  agentType: AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT;
  key: keyof VendorAssessmentControl;
  // sectionWiseData: VendorAssessmentControl[];
  uId: string;
  newValue: string;
  changesInTestingProcedure?: number;
  preventRemoval?: boolean;
};

export type updateHandleForNistCsf = {
  agentType: AgentSessionType.RISK_ASSESSMENT;
  key: keyof NistCsfControl;
  uId: string;
  newValue: string;
  changesInControl?: number;
};

export type updateHandle = { agentType: AgentSessionType | null } & (
  | updateHandleForVendorAssesment
  | updateHandlerForSoc2
  | updateHandleForNistCsf
  | updateHandlerForCustomGapAssessment
);

export const updateHandlerBasedOnAgentTypes = (params: updateHandle) => {
  const { agentType } = params;

  if (
    agentType === AgentSessionType.SOC2_GAP ||
    agentType === AgentSessionType.SOC2_GAP_TYPE1 ||
    agentType === AgentSessionType.SOC2_GAP_TYPE2
  ) {
    const { key, newValue, uId, changesInTestingProcedure } = params;
    const sectionWiseData = useCustomizeControlsStore.getState()
      .sectionWiseData as Soc2Control[];

    getCustomizeControlsActions().setDataState((prev) => {
      const newDataState = [...(prev as Soc2Control[])];
      const foundIndexOfElement = newDataState.findIndex((d) => d.uid === uId);
      if (foundIndexOfElement > -1) {
        if (key === "tsc_id") {
          let modifiedNewValue = newValue;

          if (
            modifiedNewValue.trim().length > 0 &&
            Object.values(TSCABBRIVATIONS).reduce(
              (acc, abbrivation) =>
                modifiedNewValue.startsWith(abbrivation) || acc,
              false
            ) === false
          ) {
            modifiedNewValue = `${
              TSCABBRIVATIONS[newDataState[foundIndexOfElement].tsc]
            }${modifiedNewValue}`;
          }

          newDataState[foundIndexOfElement].control_id = modifiedNewValue;
          newDataState[foundIndexOfElement].tsc_id = modifiedNewValue;
          if (newDataState[foundIndexOfElement].edited_state !== "default") {
            newDataState[foundIndexOfElement].tsc_id = modifiedNewValue;
            newDataState[foundIndexOfElement].control_id = modifiedNewValue;
          }
        }
        if (key === "control") {
          newDataState[foundIndexOfElement].control = newValue;
          newDataState[foundIndexOfElement].changesInControl =
            (newDataState[foundIndexOfElement].changesInControl ?? 0) + 1;
        }
        if (key === "category") {
          newDataState[
            foundIndexOfElement
          ].category = newValue as SecurityControlCategory;
        }
        if (key === "toe") {
          newDataState[foundIndexOfElement].toe = newValue;
          newDataState[foundIndexOfElement].tod = newValue;
          newDataState[foundIndexOfElement].changesInTestingProcedure =
            (newDataState[foundIndexOfElement].changesInTestingProcedure ?? 0) +
            (newDataState[foundIndexOfElement].changesInTestingProcedure === -1
              ? 2
              : 1);
          if (changesInTestingProcedure) {
            newDataState[
              foundIndexOfElement
            ].changesInTestingProcedure = changesInTestingProcedure;
          }
        }

        if (key === "new_control") {
          newDataState[foundIndexOfElement].new_control = newValue;
        }
        if (key === "new_tod" || key === "new_toe") {
          newDataState[foundIndexOfElement].new_tod = newValue;
          newDataState[foundIndexOfElement].new_toe = newValue;
        }

        if (
          newDataState[foundIndexOfElement].toe?.trim().length === 0 &&
          newDataState[foundIndexOfElement].control.trim().length === 0 &&
          (newDataState[foundIndexOfElement].tsc_id.trim() ===
            TSCABBRIVATIONS[newDataState[foundIndexOfElement].tsc] ||
            newDataState[foundIndexOfElement].tsc_id.trim().length === 0) &&
          newDataState[foundIndexOfElement].category.trim().length === 0
        ) {
          const removedUid = newDataState[foundIndexOfElement].uid;
          getCustomizeControlsActions().setSelectedControls((prev) => {
            const newSelectedControls = new Set(prev);
            newSelectedControls.delete(removedUid);
            return newSelectedControls;
          });

          newDataState.splice(foundIndexOfElement, 1);
        }
      } else {
        const newObject = (sectionWiseData as Soc2Control[]).find(
          (d) => d.uid === uId
        );
        if (newObject) {
          if (key === "tsc_id") {
            let modifiedNewValue = newValue;

            if (
              Object.values(TSCABBRIVATIONS).reduce(
                (acc, abbrivation) =>
                  modifiedNewValue.startsWith(abbrivation) || acc,
                false
              ) === false
            ) {
              modifiedNewValue = `${
                TSCABBRIVATIONS[newObject.tsc]
              }${modifiedNewValue}`;
            }

            newObject.control_id = modifiedNewValue;
            if (newObject.edited_state !== "default") {
              newObject.tsc_id = modifiedNewValue;
            }
          }
          if (key === "control") {
            newObject.control = newValue;
            newObject.changesInControl = 1;
          }
          if (key === "category") {
            newObject.category = newValue as SecurityControlCategory;
          }
          if (key === "toe") {
            newObject.toe = newValue;
            newObject.changesInTestingProcedure = 1;
            newObject.changesInControl = 1;
          }

          newDataState.push({
            ...newObject,
            edited_state: "custom",
          });
          setTimeout(() => {
            const d = document.getElementById(uId + key);
            d?.focus();
          }, 0);
        }
      }

      // Skip auto-save for AI-generated fields (new_*)
      if (!key.startsWith('new_')) {
        getCustomizeControlsActions().setChangesMade((prev) => prev + 1);
      }
      return newDataState;
    });
  }
  if (agentType === AgentSessionType.GAP_CUSTOM_FRAMEWORK) {
    const { key, newValue, uId, changesInTestingProcedure } = params;
    const sectionWiseData = useCustomizeControlsStore.getState()
      .sectionWiseData as Soc2Control[];

    getCustomizeControlsActions().setDataState((prev) => {
      const newDataState = [...(prev as Soc2Control[])];
      const foundIndexOfElement = newDataState.findIndex((d) => d.uid === uId);
      if (foundIndexOfElement > -1) {
        if (key) {
          (newDataState[foundIndexOfElement] as any)[key] = newValue;
        }

        if (key === "tsc_id") {
          let modifiedNewValue = newValue;

          if (
            modifiedNewValue.trim().length > 0 &&
            Object.values(TSCABBRIVATIONS).reduce(
              (acc, abbrivation) =>
                modifiedNewValue.startsWith(abbrivation) || acc,
              false
            ) === false
          ) {
            modifiedNewValue = `${
              TSCABBRIVATIONS[newDataState[foundIndexOfElement].tsc]
            }${modifiedNewValue}`;
          }

          newDataState[foundIndexOfElement].control_id = modifiedNewValue;
          newDataState[foundIndexOfElement].tsc_id = modifiedNewValue;
          if (newDataState[foundIndexOfElement].edited_state !== "default") {
            newDataState[foundIndexOfElement].tsc_id = modifiedNewValue;
            newDataState[foundIndexOfElement].control_id = modifiedNewValue;
          }
        }
        if (key === "control") {
          newDataState[foundIndexOfElement].control = newValue;
          newDataState[foundIndexOfElement].changesInControl =
            (newDataState[foundIndexOfElement].changesInControl ?? 0) + 1;
        }
        if (key === "category") {
          newDataState[
            foundIndexOfElement
          ].category = newValue as SecurityControlCategory;
        }
        if (key === "toe") {
          newDataState[foundIndexOfElement].toe = newValue;
          newDataState[foundIndexOfElement].tod = newValue;
          newDataState[foundIndexOfElement].changesInTestingProcedure =
            (newDataState[foundIndexOfElement].changesInTestingProcedure ?? 0) +
            (newDataState[foundIndexOfElement].changesInTestingProcedure === -1
              ? 2
              : 1);
          if (changesInTestingProcedure) {
            newDataState[
              foundIndexOfElement
            ].changesInTestingProcedure = changesInTestingProcedure;
          }
        }

        
        if (key === "new_tod" || key === "new_toe") {
          newDataState[foundIndexOfElement].new_tod = newValue;
          newDataState[foundIndexOfElement].new_toe = newValue;
        }

        if (
          newDataState[foundIndexOfElement].toe.trim().length === 0 &&
          newDataState[foundIndexOfElement].control.trim().length === 0 &&
          (newDataState[foundIndexOfElement].tsc_id.trim() ===
            TSCABBRIVATIONS[newDataState[foundIndexOfElement].tsc] ||
            newDataState[foundIndexOfElement].tsc_id.trim().length === 0) &&
          newDataState[foundIndexOfElement].category.trim().length === 0
        ) {
          const removedUid = newDataState[foundIndexOfElement].uid;
          getCustomizeControlsActions().setSelectedControls((prev) => {
            const newSelectedControls = new Set(prev);
            newSelectedControls.delete(removedUid);
            return newSelectedControls;
          });

          newDataState.splice(foundIndexOfElement, 1);
        }
      } else {
        const newObject = (sectionWiseData as Soc2Control[]).find(
          (d) => d.uid === uId
        );
        if (newObject) {
          if (key === "tsc_id") {
            let modifiedNewValue = newValue;

            if (
              Object.values(TSCABBRIVATIONS).reduce(
                (acc, abbrivation) =>
                  modifiedNewValue.startsWith(abbrivation) || acc,
                false
              ) === false
            ) {
              modifiedNewValue = `${
                TSCABBRIVATIONS[newObject.tsc]
              }${modifiedNewValue}`;
            }

            newObject.control_id = modifiedNewValue;
            if (newObject.edited_state !== "default") {
              newObject.tsc_id = modifiedNewValue;
            }
          }
          if (key === "control") {
            newObject.control = newValue;
            newObject.changesInControl = 1;
          }
          if (key === "category") {
            newObject.category = newValue as SecurityControlCategory;
          }
          if (key === "toe") {
            newObject.toe = newValue;
            newObject.changesInTestingProcedure = 1;
            newObject.changesInControl = 1;
          }

          newDataState.push({
            ...newObject,
            edited_state: "custom",
          });
          setTimeout(() => {
            const d = document.getElementById(uId + key);
            d?.focus();
          }, 0);
        }
      }

      // Skip auto-save for AI-generated fields (new_*)
      if (!key.startsWith('new_')) {
        getCustomizeControlsActions().setChangesMade((prev) => prev + 1);
      }
      return newDataState;
    });
  }
  if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
    const { key, newValue, uId, preventRemoval } = params;
    const sectionWiseData = useCustomizeControlsStore.getState()
      .sectionWiseData as VendorAssessmentControl[];

    getCustomizeControlsActions().setDataState((prev) => {
      const newDataState = [...(prev as VendorAssessmentControl[])];
      const foundIndexOfElement = newDataState.findIndex((d) => d.uid === uId);
      if (foundIndexOfElement > -1) {
        if (key === "category") {
          newDataState[foundIndexOfElement].category = newValue;
        }
        if (key === "question") {
          newDataState[foundIndexOfElement].question = newValue;
          newDataState[foundIndexOfElement].changesInQuestion =
            (newDataState[foundIndexOfElement].changesInQuestion ?? 0) + 1;
        }
        if (key === "new_question") {
          newDataState[foundIndexOfElement].new_question = newValue;
        }

        // Only remove if preventRemoval is not true
        if (!preventRemoval &&
          (newDataState[foundIndexOfElement].category.trim().length === 0 ||
            (newDataState[foundIndexOfElement].section ===
              "ai_security_assessment" &&
              newDataState[foundIndexOfElement].category
                .replace("AI_", "")
                .trim().length === 0)) &&
          newDataState[foundIndexOfElement].question.trim().length === 0
        ) {
          const removedUid = newDataState[foundIndexOfElement].uid;
          getCustomizeControlsActions().setSelectedControls((prev) => {
            const newSelectedControls = new Set(prev);
            newSelectedControls.delete(removedUid);
            return newSelectedControls;
          });

          newDataState.splice(foundIndexOfElement, 1);
        }
      } else {
        const newObject = (sectionWiseData as VendorAssessmentControl[]).find(
          (d) => d.uid === uId
        );
        if (newObject) {
          if (key === "category") {
            newObject.category = newValue;
          }

          if (key === "question") {
            newObject.question = newValue;
            newObject.changesInQuestion = 1;
          }

          newDataState.push({
            ...newObject,
            edited_state: "custom",
          });
          setTimeout(() => {
            const d = document.getElementById(uId + key);
            d?.focus();
          }, 0);
        }
      }

      // Skip auto-save for AI-generated fields (new_*)
      if (!key.startsWith('new_')) {
        getCustomizeControlsActions().setChangesMade((prev) => prev + 1);
      }
      return newDataState;
    });
  }
  if (agentType === AgentSessionType.RISK_ASSESSMENT) {
    const { key, newValue, uId, changesInControl } = params;
    
    getCustomizeControlsActions().setDataState((prev) => {
      return prev.map((c) => {
        if (c.uid === uId) {
          const control = c as NistCsfControl;
          const response = {
            ...control,
            edited_state:
              control.edited_state === "NEW"
                ? control.edited_state
                : "edited",
            [key]: newValue,
          } as NistCsfControl;
          if (changesInControl) {
            response.changesInControl = changesInControl;
          }
          return response;
        }
        return c;
      });
    });
    
    // Skip auto-save for AI-generated fields (new_*)
    if (!key.startsWith('new_')) {
      getCustomizeControlsActions().setChangesMade((prev) => prev + 1);
    }
  }
};



